<template>
  <div class="twitter-analytics-public-summary">
    <el-row class="mb-x2" :gutter="16">
      <el-col :span="8">
        <AnalyticsTotal
          metric="応募数"
          :value="entryCount"
          tooltip="キャンペーンへの応募数です。この数値には、重複応募も含まれます。"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="応募者数"
          :value="applicantCount"
          tooltip="キャンペーンへ応募したアカウントの数です。"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal metric="新規応募者数" :value="newApplicantCount">
          <template #tooltip-content>
            <el-tooltip class="ml-x1" placement="top">
              <div slot="content">
                同一開催アカウントで過去に実施したキャンペーン<br />
                (WEBインスタントウィン含む、インスタントウィン除く)<br />
                に応募した実績のない応募者の数です。
              </div>
              <i class="mr-x2 mdi mdi-help-circle"></i
            ></el-tooltip>
          </template>
        </AnalyticsTotal>
      </el-col>
    </el-row>
    <el-row class="mb-x2" :gutter="16">
      <el-col :span="8">
        <AnalyticsTotal
          metric="フォロワー増減数"
          :value="followerUpDown"
          tooltip="キャンペーン開催アカウントのフォロワー数の増減です。"
          :isUpDown="true"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="新規獲得フォロワー"
          :value="newFollowerCount"
          tooltip="応募者のうち、キャンペーン実施後にフォロワーとなった数です。(キャンペーンに応募していないXアカウントは含まれません。)"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="推定リーチ"
          :value="reachCount"
          tooltip="応募者ごとのフォロワー数合計です。 応募ポストのリーチ数における推測値となっております。"
        ></AnalyticsTotal>
      </el-col>
    </el-row>
    <el-row class="mb-x3" :gutter="16">
      <el-col :span="8">
        <AnalyticsTotal metric="当選数" :value="winnerCount"></AnalyticsTotal>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import { mapState } from 'pinia'
import { defineComponent } from 'vue'

import AnalyticsTotal from '@/client/components/_atoms/analytics_total'
import { useTwitterAnalyticsPublicStore } from '@/client/store/useTwitterAnalyticsPublicStore'
import { TCampaignAnalytic } from '@/typings/api/publics/twitter_campaign_analytics.type'

export default defineComponent({
  components: { AnalyticsTotal },
  computed: {
    ...mapState(useTwitterAnalyticsPublicStore, {
      campaignAnalytics: state => state.campaignAnalytics,
      columnChartData: state => state.campaignAnalytics.slice(0, state.campaignAnalytics.length - 1),
      campaign: state => state.campaign
    }),
    entryCount() {
      return this.getTotal(this.columnChartData, 'entry_count')
    },

    applicantCount() {
      return this.getTotal(this.columnChartData, 'applicant_count')
    },

    newApplicantCount() {
      return this.getTotal(this.columnChartData, 'new_applicant_count')
    },

    followerUpDown() {
      const firstFollowerCount = this.getFirstValueExceptNull(this.campaignAnalytics, 'follower_count')

      return this.getLastValue(this.campaignAnalytics, 'follower_count') - firstFollowerCount
    },

    newFollowerCount() {
      if (!this.campaignAnalytics.length) {
        return null
      }

      const data = this.campaignAnalytics
        .map(analytic => analytic.new_follower_count)
        .filter((count): count is number => Boolean(count))

      if (moment(this.campaignAnalytics[0].datetime).isSame(this.campaign?.startDatetime, 'day')) {
        return data.length ? data[data.length - 1] : 0
      }

      if (data.length === 1) {
        return 0
      }

      if (data.length > 1) {
        return data[data.length - 1] - data[0]
      }

      return null
    },

    reachCount() {
      const firstReachCount = this.campaignAnalytics[0]?.reach_count ?? 0

      return this.getLastValue(this.campaignAnalytics, 'reach_count') - firstReachCount
    },

    winnerCount() {
      return this.getTotal(this.columnChartData, 'winner_count')
    }
  },
  methods: {
    /**
     * 合計値を取得
     */
    getTotal(data: TCampaignAnalytic[], metric: string) {
      return this.addition(this.getMetricArray(data, metric))
    },

    /**
     * 足し算
     */
    addition(numbers: number[] = []): number | null {
      const values = numbers.filter(v => v !== null)

      if (!values.length) {
        return null
      }

      return numbers.map(v => this.toNumber(v)).reduce((a, c) => a + c, 0)
    },

    /**
     * 分析データから値の配列を取得
     */
    getMetricArray(insights: readonly any[], metric: string): any[] {
      return insights.map(insight => this.getMetricValue(insight, metric))
    },

    /**
     * 分析データから値を取得
     */
    getMetricValue(insight: any, metric: string): any | null {
      return isNaN(insight[metric]) ? null : insight[metric]
    },

    getFirstValueExceptNull(analytics: readonly any[], value: string) {
      const data = analytics.find(analytic => analytic[value] !== null)

      if (!data) {
        return 0
      }

      return data[value]
    },

    /**
     * 最後の値を取得
     */
    getLastValue(analytics: readonly any[], value: string) {
      const data = analytics.filter(analytic => analytic[value] !== null)

      if (data.length === 0) {
        return null
      }

      return data[data.length - 1][value]
    },

    /**
     * 数値変換
     */
    toNumber(value: any): number {
      return !isNaN(value) ? value : 0
    }
  }
})
</script>
